<script setup lang="ts">
/**
 * Настройки → Интеграции — макет 8-9 (141:786), эпик DEV-1518.
 *
 * Экран отвечает на один вопрос: как подключить агента к трекеру. Сверху
 * готовая команда, ниже ключи, внизу — что агент этим ключом делает и чего не
 * делает. Последний блок не украшение: ключ живёт до года, и человек должен
 * понимать, что именно он выдаёт.
 *
 * Кнопки «Сохранить» здесь нет намеренно: ключ выпускается диалогом, отзыв
 * применяется сразу. Откатывать черновиком нечего — как на экране репозиториев.
 *
 * Раздел личный, а не проектный: ключ принадлежит человеку. Поэтому и крошки
 * без проекта, и вход из меню пользователя, а не из основной навигации
 * (ADR-0045).
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { Check, Copy, ExternalLink } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ApiKeyDialog from '@/features/settings/integrations/components/ApiKeyDialog.vue'
import {
  listApiKeys,
  revokeApiKey,
  type ApiKey,
} from '@/features/settings/integrations/api'
import {
  connectCommand,
  formatExpiry,
  formatLastUsed,
  scopesLabel,
} from '@/features/settings/integrations/format'

const { t, locale } = useI18n()
const toast = useToast()

const keys = ref<ApiKey[]>([])
const activeLimit = ref(0)
const isLoading = ref(false)
const error = ref('')
const revokingId = ref('')
const isDialogOpen = ref(false)
const copied = ref(false)

/** Ссылка на репозиторий сервера: человек должен видеть, чему он доверяет. */
const REPO_URL = 'https://git.vpit.dev/vinprop/backend/tracker'
const REPO_LABEL = 'git.vpit.dev/vinprop/backend/tracker'

const activeCount = computed(() => keys.value.filter((key) => key.isActive).length)
const canIssue = computed(() => activeLimit.value === 0 || activeCount.value < activeLimit.value)

/** Команда подключения с плейсхолдером вместо ключа: настоящий тут не покажешь. */
const command = computed(() => connectCommand())

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    const page = await listApiKeys()
    keys.value = page.keys
    activeLimit.value = page.activeLimit
  } catch (err) {
    error.value = errorMessage(err, t('settings.integrations.errors.load'))
  } finally {
    isLoading.value = false
  }
}

const copyCommand = async () => {
  try {
    await navigator.clipboard.writeText(command.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch {
    toast.error(t('settings.integrations.errors.copy'))
  }
}

const revoke = async (key: ApiKey) => {
  revokingId.value = key.id

  try {
    await revokeApiKey(key.id)
    toast.success(t('settings.integrations.keys.revoked', { name: key.name }))
    await load()
  } catch (err) {
    toast.error(errorMessage(err, t('settings.integrations.errors.revoke')))
  } finally {
    revokingId.value = ''
  }
}

const onIssued = async () => {
  await load()
}

onMounted(load)
</script>

<template>
  <TrackerToolbar
    :breadcrumbs="[
      { label: t('settings.personal.title'), to: '/settings' },
      { label: t('settings.integrations.title') },
    ]"
  />

  <div class="integrations">
    <!-- Подключить агента: команда целиком, копируется одной кнопкой -->
    <section class="integrations__section">
      <h2 class="integrations__heading">{{ t('settings.integrations.connect.title') }}</h2>
      <p class="integrations__hint">{{ t('settings.integrations.connect.hint') }}</p>

      <div class="integrations__command">
        <code class="integrations__code">{{ command }}</code>
        <AppButton variant="secondary" size="small" @click="copyCommand">
          <component :is="copied ? Check : Copy" :size="14" />
          {{ copied ? t('settings.integrations.connect.copied') : t('settings.integrations.connect.copy') }}
        </AppButton>
      </div>

      <a class="integrations__repo" :href="REPO_URL" target="_blank" rel="noopener noreferrer">
        <ExternalLink :size="14" />
        <span class="integrations__repo-link">{{ REPO_LABEL }}</span>
        <span class="integrations__repo-note">{{ t('settings.integrations.connect.repo') }}</span>
      </a>
    </section>

    <!-- Ключи доступа -->
    <section class="integrations__section">
      <div class="integrations__keys-head">
        <div>
          <h2 class="integrations__heading">{{ t('settings.integrations.keys.title') }}</h2>
          <p class="integrations__hint">
            {{ t('settings.integrations.keys.hint', { limit: activeLimit }) }}
          </p>
        </div>

        <AppButton :disabled="!canIssue" @click="isDialogOpen = true">
          {{ t('settings.integrations.keys.issue') }}
        </AppButton>
      </div>

      <p v-if="error" class="integrations__error">{{ error }}</p>

      <p v-else-if="!isLoading && keys.length === 0" class="integrations__empty">
        {{ t('settings.integrations.keys.empty') }}
      </p>

      <table v-else class="keys">
        <thead>
          <tr>
            <th>{{ t('settings.integrations.keys.columns.name') }}</th>
            <th>{{ t('settings.integrations.keys.columns.key') }}</th>
            <th>{{ t('settings.integrations.keys.columns.access') }}</th>
            <th>{{ t('settings.integrations.keys.columns.lastUsed') }}</th>
            <th>{{ t('settings.integrations.keys.columns.expiry') }}</th>
            <th class="keys__actions-head"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="key in keys" :key="key.id" :class="{ 'keys__row--revoked': !key.isActive }">
            <td class="keys__name">{{ key.name }}</td>
            <td class="keys__value">{{ key.keyPrefix }}•••••••</td>
            <td>{{ scopesLabel(key.scopes, t) }}</td>
            <td>{{ formatLastUsed(key.lastUsedAt, locale, t) }}</td>
            <td>{{ formatExpiry(key, locale, t) }}</td>
            <td class="keys__actions">
              <button
                v-if="key.isActive"
                class="keys__revoke"
                :disabled="revokingId === key.id"
                @click="revoke(key)"
              >
                {{ t('settings.integrations.keys.revoke') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Что агент делает вашим ключом: три колонки, последняя — про запреты -->
    <section class="integrations__section">
      <h2 class="integrations__heading">{{ t('settings.integrations.capabilities.title') }}</h2>

      <div class="caps">
        <div v-for="group in ['reads', 'writes', 'cannot']" :key="group" class="caps__box">
          <p class="caps__label" :class="{ 'caps__label--danger': group === 'cannot' }">
            {{ t(`settings.integrations.capabilities.${group}.title`) }}
          </p>
          <p v-for="line in 4" :key="line" class="caps__line">
            {{ t(`settings.integrations.capabilities.${group}.items.${line - 1}`) }}
          </p>
        </div>
      </div>
    </section>
  </div>

  <ApiKeyDialog v-model:open="isDialogOpen" @issued="onIssued" />
</template>

<style scoped>
.integrations {
  padding: 28px 48px 48px;
}

.integrations__section + .integrations__section {
  margin-top: 40px;
}

.integrations__heading {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.integrations__hint {
  margin: 4px 0 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.integrations__command {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  justify-content: space-between;
  max-width: 900px;
  margin-top: var(--space-5);
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-muted);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
}

.integrations__code {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  line-height: 1.6;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-all;
}

.integrations__repo {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  margin-top: var(--space-5);
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  text-decoration: none;
}

.integrations__repo-link {
  color: var(--color-primary);
}

.integrations__repo:hover .integrations__repo-link {
  text-decoration: underline;
}

.integrations__keys-head {
  display: flex;
  gap: var(--space-4);
  align-items: flex-start;
  justify-content: space-between;
}

.integrations__error,
.integrations__empty {
  margin-top: var(--space-5);
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.integrations__error {
  color: var(--color-danger);
}

.keys {
  width: 100%;
  margin-top: var(--space-5);
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.keys th {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-muted);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--color-bg-muted);
}

.keys th:first-child {
  border-top-left-radius: var(--radius-md);
}

.keys th:last-child {
  border-top-right-radius: var(--radius-md);
}

.keys td {
  padding: var(--space-3);
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-border-default);
}

.keys__name {
  font-weight: var(--font-weight-medium);
}

.keys__value {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

/* Погашенный ключ остаётся в списке историей, но читается как выключенный. */
.keys__row--revoked td {
  color: var(--color-text-muted);
}

.keys__actions {
  text-align: right;
}

.keys__revoke {
  padding: 0;
  font: inherit;
  color: var(--color-danger);
  cursor: pointer;
  background: none;
  border: none;
}

.keys__revoke:disabled {
  color: var(--color-text-muted);
  cursor: progress;
}

.caps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
  margin-top: var(--space-5);
}

.caps__box {
  padding: var(--space-3) var(--space-4) var(--space-4);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
}

.caps__label {
  margin: 0 0 var(--space-3);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.caps__label--danger {
  color: var(--color-danger);
}

.caps__line {
  margin: 0 0 var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
}

@media (max-width: 900px) {
  .integrations {
    padding: 20px;
  }

  .caps {
    grid-template-columns: 1fr;
  }
}
</style>
